import { useCallback, useMemo } from "react"
import { useDispatch } from "react-redux"
import { useMutation, useMutationState, useQueryClient } from "@tanstack/react-query"
import type { DefaultError } from "@tanstack/react-query"
import { ConflictError } from "api/errors"
import { queryStatus } from "helpers/data-statuses"
import type { DataStatus } from "helpers/data-statuses"
import { useNotifications } from "hooks/notifications"
import { useGetQuery } from "hooks/queries"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { getSkillSettingsQueryKey } from "resources/skills"

export interface SkillSetting {
  id: string
  enabled: boolean
}

export interface SkillSettingsResponse {
  skills: SkillSetting[]
}

export const SKILL_SETTINGS_MUTATION_KEY = ["skill-settings"] as const

export interface SkillSettings {
  status: DataStatus
  isSkillEnabled: (skillId: string) => boolean
  settingFor: (skillId: string) => boolean | undefined
}

export function useSkillSettings(): SkillSettings {
  const accountAlias = useAccountAlias()
  const query = useGetQuery<SkillSettingsResponse>({ url: `/api/${accountAlias}/skills/settings`, key: getSkillSettingsQueryKey(accountAlias) })
  const disabledIds = useMemo(
    () => new Set((query.data?.skills ?? []).filter((setting) => !setting.enabled).map((setting) => setting.id)),
    [query.data],
  )
  const settingsById = useMemo(
    () => new Map((query.data?.skills ?? []).map((setting) => [setting.id, setting.enabled])),
    [query.data],
  )
  const isSkillEnabled = useCallback((skillId: string) => !disabledIds.has(skillId), [disabledIds])
  const settingFor = useCallback((skillId: string) => settingsById.get(skillId), [settingsById])
  return { status: queryStatus(query), isSkillEnabled, settingFor }
}

export interface SetSkillEnabledPayload {
  skillId: string
  skillName: string
  enabled: boolean
}

interface SetSkillEnabledContext {
  previousEnabled: boolean | undefined
}

function withSetting(settings: SkillSettingsResponse | undefined, skillId: string, enabled: boolean | undefined): SkillSettingsResponse {
  const skills = settings?.skills ?? []
  const others = skills.filter((setting) => setting.id !== skillId)
  return { skills: enabled === undefined ? others : [...others, { id: skillId, enabled }] }
}

function failureMessage({ skillName, enabled }: SetSkillEnabledPayload): string {
  return enabled
    ? `Could not turn on "${skillName}". The save failed, so Kelly still cannot use it.`
    : `Could not turn off "${skillName}". The save failed, so Kelly can still use it.`
}

function conflictMessageOf(error: unknown): string | null {
  return error instanceof ConflictError && typeof error.error === "string" ? error.error : null
}

export function useSetSkillEnabled() {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const notifs = useNotifications()
  const accountAlias = useAccountAlias()

  const { mutateAsync } = useMutation<unknown, DefaultError, SetSkillEnabledPayload, SetSkillEnabledContext>({
    mutationKey: SKILL_SETTINGS_MUTATION_KEY,
    mutationFn: ({ skillId, enabled }) =>
      new Promise((resolve, reject) => {
        dispatch(Creators.put(`/api/${accountAlias}/skills/settings/${encodeURIComponent(skillId)}`, { enabled }, resolve, reject))
      }),
    onMutate: async ({ skillId, enabled }) => {
      const key = getSkillSettingsQueryKey(accountAlias)
      await queryClient.cancelQueries({ queryKey: key })
      const previousEnabled = queryClient.getQueryData<SkillSettingsResponse>(key)?.skills.find((setting) => setting.id === skillId)?.enabled
      queryClient.setQueryData<SkillSettingsResponse>(key, (current) => withSetting(current, skillId, enabled))
      return { previousEnabled }
    },
    onError: (error, payload, context) => {
      const key = getSkillSettingsQueryKey(accountAlias)
      queryClient.setQueryData<SkillSettingsResponse>(key, (current) => withSetting(current, payload.skillId, context?.previousEnabled))
      notifs.error(conflictMessageOf(error) ?? failureMessage(payload))
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: getSkillSettingsQueryKey(accountAlias) })
    },
  })

  const pendingIds = useMutationState({
    filters: { mutationKey: SKILL_SETTINGS_MUTATION_KEY, status: "pending" },
    select: (mutation) => pendingSkillId(mutation.state.variables),
  })
  const pendingSkillIds = useMemo<ReadonlySet<string>>(() => new Set(pendingIds.filter((id): id is string => id !== null)), [pendingIds])
  return useMemo(() => ({ setSkillEnabled: mutateAsync, pendingSkillIds }), [mutateAsync, pendingSkillIds])
}

function pendingSkillId(variables: unknown): string | null {
  if (typeof variables !== "object" || variables === null || !("skillId" in variables)) return null
  const { skillId } = variables
  return typeof skillId === "string" ? skillId : null
}
